import { roleLabel } from 'common/share-dialog';
import { vaultInvitationRole } from 'helpers/sharing';
import {
  ChevronRightSmallIcon,
  CrossIcon,
  ImageIcon,
  ImagePlusIcon,
  InfoIcon,
  UserPlusCircleIcon,
} from 'assets/icons';
import { VaultCover } from 'common/vault-cover';
import { VaultCollaboratorRole } from 'common/enums';
import CypressIds from 'cypressIds';
import { trackCustomEvent } from 'helpers/analytics';
import {
  checkTitleAndGetError,
  removeEmailsAlreadyAddedErrors,
} from 'helpers/new-vault-helper';
import { classnames } from 'helpers/utils';
import { cloneDeep } from 'lodash';
import { Contributor } from 'models/contributor';
import { ChangeEvent, Dispatch, useRef, useState } from 'react';
import { CollaboratorsModal } from '../collaborators-modal';
import { VaultNewStateType, NewVaultAction } from '../../vault-new-reducer';
import styles from './step-information.module.scss';

type StepInformationProps = {
  newVaultState: VaultNewStateType;
  newVaultDispatch: Dispatch<NewVaultAction>;
};

type OptionalSection = 'collaborators' | 'cover';

const StepInformation: React.FC<StepInformationProps> = ({
  newVaultState,
  newVaultDispatch,
}) => {
  const idContributorAux = useRef(1);
  const { vault, errors, coverPreviewUrl } = newVaultState;
  const [openSection, setOpenSection] = useState<OptionalSection | null>(null);
  const [showCollaborators, setShowCollaborators] = useState(false);

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    newVaultDispatch({
      type: 'VAULT_INPUT',
      field: e.target.name,
      value: e.target.value,
    });
  };

  const handleIsLoading = (isLoading: boolean) => {
    newVaultDispatch({
      type: 'IS_LOADING',
      isLoading,
    });
  };

  const handleImageUpload = (
    signedId: string | undefined,
    previewUrl: string | null,
  ) => {
    newVaultDispatch({
      type: 'UPLOAD_IMAGE',
      coverSignedId: signedId,
      previewUrl,
    });
  };

  // The modal collects a complete collaborator, so unlike `addNewContributor`
  // this appends a filled-in row rather than a blank one to edit in place.
  const handleAddCollaborator = (collaborator: {
    name: string;
    email: string;
    role: VaultCollaboratorRole;
    canViewVault: boolean;
  }) => {
    const contributorsCopy = cloneDeep(vault.contributors);

    contributorsCopy.push(
      new Contributor({
        idContributorAux: idContributorAux.current,
        id: -1,
        contributions: 0,
        ...collaborator,
      }),
    );

    const newErrorsList = cloneDeep(newVaultState.errors);
    newErrorsList.contributors.push({
      idContributorAux: idContributorAux.current,
      name: '',
      email: '',
      contributions: 0,
    });

    idContributorAux.current += 1;

    newVaultDispatch({
      type: 'UPDATE_CONTRIBUTORS',
      contributors: contributorsCopy,
    });
    newVaultDispatch({ type: 'UPDATE_ERRORS', errors: newErrorsList });
    setShowCollaborators(false);
  };

  const deleteContributorFromArray = (
    contributors: ContributorType[],
    contDeleted: ContributorType,
  ) => {
    const contributorsCopy = cloneDeep(contributors);

    const contributorsUpdated = contributorsCopy.filter(
      (contri: ContributorType) =>
        contri.idContributorAux !== contDeleted.idContributorAux,
    );

    return contributorsUpdated;
  };

  const deleteContributorFromErrorsArray = (
    errs: NewVaultErrorsType,
    contributorsList: ContributorType[],
    contDeleted: ContributorType,
  ) => {
    const contributorsErrorsFiltered = errs.contributors.filter(
      (contributorErrors: ContributorType) =>
        contributorErrors.idContributorAux !== contDeleted.idContributorAux,
    );

    const newContributorsErrorsList = removeEmailsAlreadyAddedErrors(
      contributorsList,
      contributorsErrorsFiltered,
    );

    return newContributorsErrorsList;
  };

  const handleDeleteContributor = (
    contributors: ContributorType[],
    errs: NewVaultErrorsType,
    contDeleted: ContributorType,
  ) => {
    trackCustomEvent('confirmRemoveContributor', {
      step: 'createVault',
      contributorEmail: contDeleted.email,
      contributorName: contDeleted.name,
    });

    const newContributorList = deleteContributorFromArray(
      contributors,
      contDeleted,
    );
    newVaultDispatch({
      type: 'UPDATE_CONTRIBUTORS',
      contributors: newContributorList,
    });

    const newContributorsErrorsList = deleteContributorFromErrorsArray(
      errs,
      newContributorList,
      contDeleted,
    );

    newVaultDispatch({
      type: 'UPDATE_ERRORS',
      errors: {
        ...newVaultState.errors,
        contributors: newContributorsErrorsList,
      },
    });
  };

  const handleValidateTitle = (newValue: string) => {
    const titleErrorUpdated = checkTitleAndGetError(newValue);

    newVaultDispatch({
      type: 'UPDATE_ERRORS',
      errors: { ...newVaultState.errors, title: titleErrorUpdated },
    });
  };

  // The design shows only a count on the row, but a collaborator added by
  // mistake still needs a way out, so added people are listed beneath it.
  const showCollaboratorList = () => {
    if (vault.contributors.length === 0) return null;

    return (
      <ul className={styles.collaboratorList}>
        {vault.contributors.map((contributor: ContributorType) => (
          <li
            key={contributor.idContributorAux}
            className={styles.collaborator}
          >
            <span className={styles.collaboratorAvatar}>
              {(contributor.name || contributor.email)
                .trim()
                .charAt(0)
                .toUpperCase() || '?'}
            </span>
            <span className={styles.collaboratorText}>
              <span className={styles.collaboratorName}>
                {contributor.name || contributor.email}
              </span>
              <span className={styles.collaboratorMeta}>
                {roleLabel(vaultInvitationRole(contributor))}
                {contributor.role === VaultCollaboratorRole.contributor &&
                  contributor.canViewVault === false &&
                  ' · own additions only'}
              </span>
            </span>
            <button
              type="button"
              aria-label={`Remove ${contributor.name || contributor.email}`}
              className={styles.collaboratorRemove}
              onClick={() =>
                handleDeleteContributor(vault.contributors, errors, contributor)
              }
            >
              <CrossIcon />
            </button>
          </li>
        ))}
      </ul>
    );
  };

  // The two optional extras are collapsed by default so the screen leads with
  // the single required field. Opening one reveals the existing editor in
  // place rather than pushing the user to another screen.
  const showDisclosure = (
    section: OptionalSection,
    Icon: React.FC<React.SVGProps<SVGSVGElement>>,
    title: string,
    description: string,
    preview?: React.ReactNode,
    body?: React.ReactNode,
  ) => {
    const isOpen = openSection === section;

    return (
      <div
        className={classnames(styles.disclosure, {
          [styles.disclosureOpen]: isOpen,
        })}
      >
        <button
          type="button"
          className={styles.disclosureHeader}
          aria-expanded={isOpen}
          onClick={() => setOpenSection(isOpen ? null : section)}
        >
          <span className={styles.disclosureIcon}>
            <Icon />
          </span>
          <span className={styles.disclosureText}>
            <span className={styles.disclosureTitle}>{title}</span>
            <span className={styles.disclosureDescription}>{description}</span>
          </span>
          {preview}
          <ChevronRightSmallIcon
            className={classnames(styles.disclosureChevron, {
              [styles.disclosureChevronOpen]: isOpen,
            })}
          />
        </button>

        {isOpen && <div className={styles.disclosureBody}>{body}</div>}
      </div>
    );
  };

  const showCoverPreview = () => (
    <span className={styles.coverPreview}>
      {coverPreviewUrl ? (
        <img src={coverPreviewUrl} alt="" />
      ) : (
        <ImageIcon className={styles.coverPreviewPlaceholder} />
      )}
    </span>
  );

  return (
    <div className={styles.container}>
      {showCollaborators && (
        <CollaboratorsModal
          onAdd={handleAddCollaborator}
          onClose={() => setShowCollaborators(false)}
        />
      )}

      <div className={styles.card}>
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Vault name</h2>
          <input
            id="title"
            name="title"
            type="text"
            className={classnames(styles.nameInput, {
              [styles.nameInputError]: !!errors.title,
            })}
            value={vault.title}
            placeholder="e.g. Grandpa's Life"
            onChange={onChange}
            onBlur={(e: ChangeEvent<HTMLInputElement>) => {
              handleValidateTitle(e.target.value);
            }}
            data-cy={CypressIds.vaultName}
          />
          {errors.title && (
            <span className={styles.nameError}>{errors.title}</span>
          )}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Make it yours</h2>
          <p className={styles.sectionSubtitle}>
            These are optional. You can always update them later.
          </p>

          <div className={styles.disclosures}>
            {/* Collaborators open a dialog rather than expanding in place —
                the editor is too tall to sit inside this card. */}
            <div className={styles.disclosure}>
              <button
                type="button"
                className={styles.disclosureHeader}
                onClick={() => setShowCollaborators(true)}
              >
                <span className={styles.disclosureIcon}>
                  <UserPlusCircleIcon />
                </span>
                <span className={styles.disclosureText}>
                  <span className={styles.disclosureTitle}>
                    Add collaborators
                  </span>
                  <span className={styles.disclosureDescription}>
                    Invite others to contribute or help edit this Vault.
                  </span>
                </span>
                {vault.contributors.length > 0 && (
                  <span className={styles.disclosureCount}>
                    {vault.contributors.length}
                  </span>
                )}
                <ChevronRightSmallIcon className={styles.disclosureChevron} />
              </button>

              {showCollaboratorList()}
            </div>

            {showDisclosure(
              'cover',
              ImagePlusIcon,
              'Add cover photo',
              'Add an image to personalize your Vault',
              showCoverPreview(),
              <VaultCover
                setIsLoading={handleIsLoading}
                setImage={handleImageUpload}
              />,
            )}
          </div>
        </section>

        <div className={styles.archiveNote}>
          <InfoIcon className={styles.archiveNoteIcon} />
          <p className={styles.archiveNoteText}>
            <b>Each Vault is a separate archive.</b> Items can&apos;t currently
            be moved between Vaults.
          </p>
        </div>
      </div>
    </div>
  );
};

export { StepInformation };
